import { Badge } from "@/components/ui/badge";
import Button3D from "@/components/orders/Button3D";
import { TEMPLATE_LABEL, TEMPLATE_OPTIONS, describeProductLine } from "./templates";
import type { OrderProductLine, TemplateKey } from "./types";

interface WizardStageProps {
  productLines: OrderProductLine[];
  currentIndex: number;
  choices: Record<string, TemplateKey | undefined>;
  onPickTemplate: (templateKey: TemplateKey) => void;
  onBack: () => void;
}

export const WizardStage = ({
  productLines,
  currentIndex,
  choices,
  onPickTemplate,
  onBack,
}: WizardStageProps) => {
  const currentProductLine = productLines[currentIndex];

  const groupedChoices = new Map<TemplateKey, OrderProductLine[]>();
  for (const productLine of productLines) {
    const selectedTemplate = choices[productLine.id];
    if (!selectedTemplate) continue;

    const existingGroup = groupedChoices.get(selectedTemplate) ?? [];
    existingGroup.push(productLine);
    groupedChoices.set(selectedTemplate, existingGroup);
  }

  if (!currentProductLine) return null;

  return (
    <div className="space-y-4">
      <div className="rounded-md border bg-muted/30 p-3">
        <div className="mb-2 text-sm font-medium text-foreground">Live overzicht</div>
        {groupedChoices.size === 0 ? (
          <div className="text-xs text-muted-foreground">Nog geen sjablonen gekozen.</div>
        ) : (
          <div className="space-y-2">
            {Array.from(groupedChoices.entries()).map(([templateKey, lines]) => (
              <div key={templateKey} className="rounded-md border bg-background p-2">
                <div className="mb-1 flex items-center gap-2">
                  <Badge variant="secondary" className="text-[10px]">
                    {lines.length}x {TEMPLATE_LABEL[templateKey]}
                  </Badge>
                </div>
                <ul className="space-y-1 pl-4 text-xs text-muted-foreground">
                  {lines.map((line) => (
                    <li key={line.id} className="list-disc">
                      {describeProductLine(line)}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-md border bg-background p-3">
        <div className="text-xs text-muted-foreground">
          Product {currentIndex + 1} van {productLines.length}
        </div>
        <div className="mt-1 text-sm font-medium text-foreground">
          {describeProductLine(currentProductLine)}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          Kies het juiste sjabloon voor dit product.
        </div>
      </div>

      <div className="grid gap-2">
        {TEMPLATE_OPTIONS.map((template) => (
          <Button3D
            key={template.key}
            tone={choices[currentProductLine.id] === template.key ? "primary" : "neutral"}
            className="w-full justify-start"
            onClick={() => onPickTemplate(template.key)}
          >
            {template.label}
          </Button3D>
        ))}
      </div>

      {currentIndex > 0 && (
        <div>
          <Button3D tone="neutral" onClick={onBack}>
            Vorig product
          </Button3D>
        </div>
      )}
    </div>
  );
};
